import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { MaskTextLoad, FadeUp } from "@/components/Reveal";
import { ROLES, IMAGES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", role: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.role) {
      toast.error("Please share your name, email and who you are.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/registrations`, form);
      setDone(true);
      toast.success("Welcome to the movement. We'll be in touch.");
      setForm({ name: "", email: "", role: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-ink/20 py-4 font-sans text-lg text-ink placeholder-ink/35 focus:outline-none focus:border-magenta transition-colors duration-300";

  return (
    <div className="bg-cream min-h-screen">
      <section className="relative grid lg:grid-cols-2 min-h-screen">
        {/* Left — framed photo + message */}
        <div className="relative hidden lg:flex flex-col justify-center gap-8 bg-[#1B0812] px-12 xl:px-16 py-24 overflow-hidden">
          <div className="absolute inset-0 grain pointer-events-none" />
          <div className="absolute -top-16 -left-16 h-72 w-72 rounded-full brand-gradient blur-[120px] opacity-25 pointer-events-none" />
          <div className="relative z-10 w-full">
            <div className="relative overflow-hidden rounded-3xl aspect-[3/2] card-shadow ring-1 ring-white/10">
              <img src={IMAGES.standing} alt="Choices & Decisions attendees" className="h-full w-full object-cover object-center" />
            </div>
            <p className="mt-8 font-serif italic font-light text-2xl xl:text-3xl leading-snug text-cream max-w-md">
              “Someone, somewhere, is waiting for the woman you will become.”
            </p>
            <p className="mt-5 font-display text-xs uppercase tracking-[0.25em] text-coral">Believe. Become. Lead.</p>
          </div>
        </div>

        {/* Right — form */}
        <div className="flex items-center px-5 md:px-10 lg:px-16 pt-32 pb-20">
          <div className="w-full max-w-xl mx-auto">
            <p className="font-display text-xs uppercase tracking-[0.3em] text-magenta mb-6">Register Interest</p>
            <h1 className="font-serif font-light text-5xl md:text-6xl leading-[0.9] text-ink">
              <MaskTextLoad lines={["Take your", "place in it."]} />
            </h1>
            <FadeUp delay={0.4}>
              <p className="mt-6 font-sans text-base md:text-lg text-ink/60 leading-relaxed">
                Whether you are a parent, a young woman, an educator or a sponsor — tell us who you are and we will walk beside you.
              </p>
            </FadeUp>

            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="register-success"
                className="mt-12 rounded-3xl border border-ink/10 bg-white card-shadow p-10 text-center"
              >
                <div className="mx-auto h-16 w-16 rounded-full brand-gradient flex items-center justify-center">
                  <Check size={30} className="text-white" />
                </div>
                <h2 className="mt-6 font-serif text-3xl text-ink">You're part of it now.</h2>
                <p className="mt-3 font-sans text-ink/60">
                  Thank you for believing in the next generation. We'll be in touch soon.
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="register-another"
                  className="mt-8 font-display text-xs uppercase tracking-[0.16em] text-magenta hover:text-ink transition-colors"
                >
                  Register someone else
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="mt-12 space-y-8" data-testid="register-form">
                <div>
                  <label className="font-display text-xs uppercase tracking-[0.18em] text-ink/40">Your name</label>
                  <input data-testid="register-name" className={inputCls} placeholder="Full name" value={form.name} onChange={update("name")} />
                </div>
                <div>
                  <label className="font-display text-xs uppercase tracking-[0.18em] text-ink/40">Email</label>
                  <input data-testid="register-email" type="email" className={inputCls} placeholder="you@email.com" value={form.email} onChange={update("email")} />
                </div>
                <div>
                  <label className="font-display text-xs uppercase tracking-[0.18em] text-ink/40">I am...</label>
                  <select data-testid="register-role" className={`${inputCls} appearance-none cursor-pointer`} value={form.role} onChange={update("role")}>
                    <option value="" disabled>Select one</option>
                    {ROLES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="font-display text-xs uppercase tracking-[0.18em] text-ink/40">A message (optional)</label>
                  <textarea data-testid="register-message" rows={3} className={`${inputCls} resize-none`} placeholder="Tell us what brings you here..." value={form.message} onChange={update("message")} />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="register-submit-button"
                  className="group inline-flex items-center gap-3 rounded-full brand-gradient px-9 py-4 font-display text-sm uppercase tracking-[0.14em] font-bold text-white transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_30px_rgba(232,38,111,0.4)] disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Join the Movement"}
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
